import { useEffect, useRef, useState } from "react";
import { ASSIGN_MODES } from "../MentorAssignPanel/constants";
import { useFetchMentorList } from "../services/api";
import {
  assignedMentorId,
  buildMentorListRequest,
  MENTOR_LIST_MODES,
} from "../services/batchPayload";
import { readEnvelope } from "../services/envelope";
import { mentorListMapper } from "../services/mentorMapper";

/* Long enough that typing a schedule or a search term does not fire per keystroke. */
const DEBOUNCE_MS = 500;

/* `results: undefined` is "no search has run"; [] is "a search found nothing". */
const EMPTY_SEARCH = { query: "", results: undefined, pickId: null, pick: null };

/* Choosing a mentor IS the assignment, as the design shows it — there is no
   separate assign control: whichever route a free mentor is chosen by, it is the
   one the batch gets, and choosing another route takes it back. An occupied mentor
   cannot be assigned, so choosing one assigns nobody. */
const isAssignable = (mentor) => !!mentor && mentor?.status !== "occupied";

/**
 * One mentor section's state over the backend's mentor list (E2): fetch the
 * list for a schedule, follow its preselection, search, check a searched pick,
 * and resolve the one mentor that is assigned directly.
 *
 * The list is valid only for the inputs it was fetched for. When they change
 * (schedule, count, course, stitch configuration) the list and every choice made
 * on it are dropped, and nothing can be chosen until it is fetched again
 * (R-05-01, R-07-08) — a choice on a stale answer is what the owner ruled out.
 *
 * @param {Object} input
 * @param {Object|null} input.request - E2's fields except mode/term/mentorId
 *   (studentId, orderId, courseId, startDate, days, duration, sessionCount, batchId),
 *   or null while they are incomplete
 * @param {boolean} [input.auto] - fetch by itself, debounced, whenever the request
 *   is complete and changed; otherwise only through `fetchList`
 * @returns {{ state: Object, derived: Object, handlers: Object }}
 */
export const useMentorSelection = ({ request, auto = false }) => {
  /* The list and the inputs it answers travel together: either both or neither. */
  const [fetched, setFetched] = useState({ list: null, key: null });
  const [error, setError] = useState("");
  const [searchError, setSearchError] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [mode, setMode] = useState(null);
  const [assigned, setAssigned] = useState(false);
  const [modal, setModal] = useState({ isOpen: false, choiceId: null });
  const [bestPickId, setBestPickId] = useState(null);
  const [search, setSearch] = useState(EMPTY_SEARCH);

  const { list, key: fetchedKey } = fetched;
  const { query: searchQuery, results: searchResults, pickId: searchPickId, pick: searchPick } =
    search;

  const { mutate: fetchMentors, loading: isFetching } = useFetchMentorList();
  const { mutate: searchMentors } = useFetchMentorList();
  const { mutate: checkMentor, loading: isCheckingPick } = useFetchMentorList();

  const requestKey = request ? JSON.stringify(request) : null;
  const latestKey = useRef(requestKey);
  latestKey.current = requestKey;
  /* The searched mentor whose check answer may land; a later pick supersedes it. */
  const latestPickId = useRef(null);
  const latestTerm = useRef(null);

  const clearChoices = () => {
    setMode(null);
    setAssigned(false);
    setModal({ isOpen: false, choiceId: null });
    setBestPickId(null);
    setSearch(EMPTY_SEARCH);
  };

  const fetchList = async () => {
    if (!request) return;
    const key = requestKey;
    setError("");
    try {
      const raw = await fetchMentors({
        data: buildMentorListRequest({ ...request, mode: MENTOR_LIST_MODES.LIST }),
      });
      /* Only the answer for the current inputs may land. */
      if (latestKey.current !== key) return;
      const vm = mentorListMapper(raw);
      clearChoices();
      setFetched({ list: vm, key });
      /* The screen follows the list's preselection (R-05-10), assigned when it is
         the known mentor and they are free. */
      setMode(vm?.preselectedOption ?? null);
      setAssigned(vm?.preselectedOption === ASSIGN_MODES.KNOWN && isAssignable(vm?.known));
    } catch (thrown) {
      if (latestKey.current !== key) return;
      setFetched({ list: null, key: null });
      setError(readEnvelope(thrown)?.message);
    }
  };

  /* A change of inputs drops the list and the choices made on it. */
  useEffect(() => {
    if (fetchedKey && fetchedKey !== requestKey) {
      setFetched({ list: null, key: null });
      clearChoices();
    }
    /* An error belongs to the inputs it was raised for; new inputs start clean. */
    setError("");
    if (!auto || !requestKey || requestKey === fetchedKey) return undefined;
    const timer = setTimeout(fetchList, DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey, auto]);

  /* Search runs against the backend's pool (R-05-07), debounced. */
  useEffect(() => {
    const term = searchQuery?.trim?.();
    if (!term || !request || mode !== ASSIGN_MODES.SEARCH) {
      /* A cleared or abandoned box has no latest term, so an answer still in
         flight for the previous one can no longer land on it — it would put
         results back under an empty box, pickable. */
      latestTerm.current = null;
      /* Only when there is something to clear: a fresh object on every mode change
         re-renders the section a frame after a mentor is chosen, and React 17
         flushes that render at the start of the next input event — resetting a
         controlled field (the mentor-change reason) picked in that frame. */
      setSearch((current) => (current?.results === undefined ? current : { ...current, results: undefined }));
      setSearchError("");
      setIsSearching(false);
      return undefined;
    }
    const key = requestKey;
    latestTerm.current = term;
    /* Two searches can be in flight at once, so only the newest term's answer
       may land — an older one would replace newer results. */
    const isLatest = () => latestKey.current === key && latestTerm.current === term;
    setIsSearching(true);
    const timer = setTimeout(async () => {
      try {
        const raw = await searchMentors({
          data: buildMentorListRequest({ ...request, mode: MENTOR_LIST_MODES.SEARCH, term }),
        });
        if (isLatest()) {
          setSearch((current) => ({ ...current, results: mentorListMapper(raw)?.search ?? [] }));
          setSearchError("");
          setIsSearching(false);
        }
      } catch (thrown) {
        /* The message belongs beside the box that failed, not at the top of the
           card, and the results go empty rather than staying blank. */
        if (isLatest()) {
          setSearch((current) => ({ ...current, results: [] }));
          setSearchError(readEnvelope(thrown)?.message);
          setIsSearching(false);
        }
      }
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      setIsSearching(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, mode, requestKey]);

  /* A searched mentor is checked against the schedule when picked (R-05-05). */
  const pickSearched = async (mentorId) => {
    latestPickId.current = mentorId;
    setSearch((current) => ({ ...current, pickId: mentorId, pick: null }));
    setAssigned(false);
    setError("");
    const key = requestKey;
    const isLatest = () => latestKey.current === key && latestPickId.current === mentorId;
    try {
      const raw = await checkMentor({
        data: buildMentorListRequest({ ...request, mode: MENTOR_LIST_MODES.CHECK, mentorId }),
      });
      if (!isLatest()) return;
      const checked = mentorListMapper(raw)?.search?.[0] ?? null;
      setSearch((current) => ({ ...current, pick: checked }));
      setAssigned(isAssignable(checked));
      /* An answer with no mentor would otherwise leave a picked radio and nothing else. */
      if (!checked) setError("That mentor could not be checked against this schedule.");
    } catch (thrown) {
      if (isLatest()) {
        setSearch((current) => ({ ...current, pickId: null }));
        setError(readEnvelope(thrown)?.message);
      }
    }
  };

  /* Changing group abandons the previous group's assignment — "Choose any ONE
     method to assign" — and takes up the new group's mentor if it already has one.
     A best-matched list of exactly one mentor has nothing to choose between, so
     that mentor is the pick without opening the popup. */
  const changeMode = (next) => {
    setMode(next);
    const onlyBest = list?.bestMatched?.length === 1 ? list?.bestMatched?.[0] : null;
    if (next === ASSIGN_MODES.BEST && onlyBest) setBestPickId(onlyBest?.id);
    const groupMentor = {
      [ASSIGN_MODES.KNOWN]: list?.known,
      [ASSIGN_MODES.BEST]: onlyBest ?? list?.bestMatched?.find((mentor) => mentor?.id === bestPickId),
      [ASSIGN_MODES.SEARCH]: search?.pick,
    }?.[next];
    setAssigned(isAssignable(groupMentor));
  };

  const bestPick = list?.bestMatched?.find((mentor) => mentor?.id === bestPickId) ?? null;
  const selection = { mode, assigned, known: list?.known, bestPick, searchPick };
  const mentorId = assignedMentorId(selection);
  const chosen =
    { [ASSIGN_MODES.KNOWN]: list?.known, [ASSIGN_MODES.BEST]: bestPick, [ASSIGN_MODES.SEARCH]: searchPick }?.[mode] ??
    null;

  return {
    state: { list, mode, assigned, listOpen: modal?.isOpen, listChoiceId: modal?.choiceId, searchQuery, searchPickId },
    derived: {
      hasList: !!list,
      isFetching,
      /* On its way: waiting out the debounce or in flight. The design's "Fetching…"
         pill follows this, so it does not blink off between the two. */
      isPending: isFetching || (auto && !!requestKey && requestKey !== fetchedKey && !error),
      isCheckingPick,
      isSearching,
      error,
      searchError,
      known: list?.known ?? null,
      bestMatched: list?.bestMatched ?? [],
      bestPick,
      searchResults,
      searchPick,
      mentorId,
      mentorName: mentorId ? (chosen?.name ?? "") : "",
      canFetch: !!request,
    },
    handlers: {
      fetchList,
      onModeChange: changeMode,
      onOpenList: () => setModal({ isOpen: true, choiceId: bestPickId }),
      onCloseList: () => setModal((current) => ({ ...current, isOpen: false })),
      onSelectListMentor: (id) => setModal((current) => ({ ...current, choiceId: id })),
      onConfirmList: () => {
        setBestPickId(modal?.choiceId);
        setMode(ASSIGN_MODES.BEST);
        /* Every best-matched mentor is free on the chosen slots (R-05-06). */
        setAssigned(modal?.choiceId !== null && modal?.choiceId !== undefined);
        setModal((current) => ({ ...current, isOpen: false }));
      },
      onSearchChange: (query) => {
        latestPickId.current = null;
        setSearch((current) => ({ ...current, query, pickId: null, pick: null }));
        setSearchError("");
        setAssigned(false);
      },
      onSearchPick: pickSearched,
    },
  };
};

export default useMentorSelection;
